import { DecimalPipe } from '@angular/common';
import { Component, effect, inject, input, model, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable } from 'rxjs';
import { AUTOSAVE_DEBOUNCE_MS, SELECT_FONT_SIZE_PX, SELECT_MIN_WIDTH_PX, SELECT_PADDING_PX } from '../../app.constants';
import { ChargerSettingsService } from '../../chargers/charger-settings.service';
import { Cpms, CpmsWithMetadata, DaySchedule, NumberSettingWithMetadata, SettingType, SettingWithMetadata, StringSettingMetadataOnly, ValueWithMetadata } from '../../models';
import { PropertyBooleanTranslatePipe, PropertyOptionTranslatePipe } from '../translate/property-translate.pipe';


@Component({
	selector: 'app-property',
	imports: [MatListModule, MatIconModule, MatFormFieldModule, MatSelectModule, MatSlideToggleModule, MatInputModule, DecimalPipe, MatButtonModule, MatTooltipModule, PropertyOptionTranslatePipe, PropertyBooleanTranslatePipe],
	providers: [PropertyOptionTranslatePipe],
	templateUrl: './property.component.html',
	styleUrl: './property.component.scss'
})
export class PropertyComponent {
	id = input.required<string>();
	type = input.required<SettingType>();
	value = model.required<ValueWithMetadata>();
	metadata = input<StringSettingMetadataOnly>();
	saveCallback = input.required<(setting: SettingWithMetadata) => Observable<boolean>>();

	isChangeAllowed = signal<boolean>(false);
	saveFailed = signal<boolean>(false);
	isCustomCpms = signal<boolean>(false);
	cpmsSelectValue = signal<string>('');
	customCpmsName = signal<string>('');
	customCpmsUrl = signal<string>('wss://');

	chargerSettingService = inject(ChargerSettingsService);
	private optionTranslatePipe = inject(PropertyOptionTranslatePipe);

	private canvas = document.createElement('canvas');

	private originalValue: ValueWithMetadata | null = null;
	private timeout: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		effect(() => {
			const metadataIsChangeAllowed = this.metadata()?.isChangeAllowed;
			const valueWithMetadata = this.value();
			if (metadataIsChangeAllowed !== undefined) {
				this.isChangeAllowed.set(metadataIsChangeAllowed);
			} else {
				if ((valueWithMetadata) && ('isChangeAllowed' in valueWithMetadata) && (valueWithMetadata.isChangeAllowed !== undefined)) {
					this.isChangeAllowed.set(valueWithMetadata.isChangeAllowed);
				} else {
					this.isChangeAllowed.set(false);
				}
			}
		});

		effect(() => {
			if (this.type() === 'Cpms') {
				const valueWithMetadata = this.chargerSettingService.returnCpmsWithMetaData(this.value());
				if (valueWithMetadata?.value) {
					const centralSystem = valueWithMetadata.value.centralSystem;
					const isKnown = valueWithMetadata.allowedValues?.some(c => c.name === centralSystem) ?? false;
					if (centralSystem && !isKnown) {
						this.isCustomCpms.set(true);
						this.cpmsSelectValue.set('__custom__');
						this.customCpmsName.set(centralSystem);
						this.customCpmsUrl.set(valueWithMetadata.value.url);
					} else {
						this.isCustomCpms.set(false);
						this.cpmsSelectValue.set(centralSystem);
					}
				}
			}
		});
	}

	onCpmsSelectionChange(selectedValue: string, valueWithMetadata: CpmsWithMetadata): void {
		if (selectedValue === '__custom__') {
			this.isCustomCpms.set(true);
			this.cpmsSelectValue.set('__custom__');
			this.customCpmsName.set('');
			this.customCpmsUrl.set('wss://');
		} else {
			this.isCustomCpms.set(false);
			this.cpmsSelectValue.set(selectedValue);
			if (valueWithMetadata.value) {
				valueWithMetadata.value.centralSystem = selectedValue;
			}
			this.saveProperty(valueWithMetadata);
		}
	}

	saveCustomCpms(valueWithMetadata: CpmsWithMetadata): void {
		if (valueWithMetadata.value) {
			valueWithMetadata.value.centralSystem = this.customCpmsName();
			valueWithMetadata.value.url = this.customCpmsUrl();
		}
		this.saveProperty(valueWithMetadata);
	}


	saveProperty(value: ValueWithMetadata) {
		let setting = {
			id: this.id(),
			type: this.type(),
			valueWithMetadata: value
		}
		console.log('saveProperty():', setting);
		this.isChangeAllowed.set(false);
		this.saveCallback()(setting).subscribe(saveSuccess => {
			console.log('saveProperty() result:', saveSuccess);
			this.isChangeAllowed.set(true);
			if (saveSuccess) {
				this.saveFailed.set(false);
			} else {
				this.saveFailed.set(true);
			}
		})
	}

	cancelEdit() {
		let originalValue = this.originalValue;
		if (originalValue) {
			this.value.set(originalValue);
		}
		this.saveFailed.set(false);
	}


	updateNumber(valueWithMetadata: NumberSettingWithMetadata, delta: number): void {
		const newValue = valueWithMetadata.value + delta;

		if (newValue < valueWithMetadata.lowerLimit || newValue > valueWithMetadata.upperLimit) {
			return;
		}

		valueWithMetadata.value = newValue;

		console.log('updateNumber(): adjust...');
		if (this.timeout) {
			clearTimeout(this.timeout);
		}
		this.timeout = setTimeout(() => {
			console.log('updateNumber(): save now');
			this.saveProperty(this.value());
		}, AUTOSAVE_DEBOUNCE_MS);
	}

	updateFreeString(): void {
		console.log('updateFreeString(): adjust...');
		if (this.timeout) {
			clearTimeout(this.timeout);
		}
		this.timeout = setTimeout(() => {
			console.log('updateFreeString(): save now');
			this.saveProperty(this.value());
		}, AUTOSAVE_DEBOUNCE_MS);
	}


	addDaySchedulePeriod(daySchedule: DaySchedule): void {
		console.log('addDaySchedulePeriod() before:', daySchedule);
		daySchedule.push(daySchedule[0] ? {...daySchedule[0]} : {...{beginTimeHour: 9, beginTimeMinute: 0, endTimeHour: 17, endTimeMinute: 0, chargingMode: 'Basic' }});
		console.log('addDaySchedulePeriod() after:', daySchedule);
	}

	deleteDaySchedulePeriod(daySchedule: DaySchedule, index: number): void {
		console.log('deleteDaySchedulePeriod() before:', daySchedule, 'index:', index);
		daySchedule.splice(index, 1);
		console.log('deleteDaySchedulePeriod() after:', daySchedule);
	}

	selectWidth(allowedValues: string[]): string {
		const ctx = this.canvas.getContext('2d');
		if (!ctx || !allowedValues.length) return `${SELECT_MIN_WIDTH_PX}px`;
		ctx.font = `${SELECT_FONT_SIZE_PX}px Roboto, sans-serif`;
		const maxPx = Math.max(...allowedValues.map(t => ctx.measureText(t).width));
		return `${Math.max(Math.ceil(maxPx) + SELECT_PADDING_PX, SELECT_MIN_WIDTH_PX)}px`;
	}

	selectWidthFromOptions(allowedValues?: string[]): string {
		return this.selectWidth((allowedValues ?? []).map(v => this.optionTranslatePipe.transform(v)));
	}

	selectWidthFromCpms(allowedValues?: Cpms[]): string {
		return this.selectWidth((allowedValues ?? []).map(c => c.name));
	}

}
